import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, Trash2, RefreshCw, Edit3, Archive, EyeOff, RotateCcw, Megaphone, Pin, PinOff, QrCode } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import TownQRModal from '../components/TownQRModal';
import AdminCommunityReportsPanel from '../components/admin/AdminCommunityReportsPanel';
import AdminIncidentsPanel from '../components/admin/AdminIncidentsPanel';

const STATUS_BADGES = {
  pending: 'bg-amber-50 text-amber-800 ring-amber-200',
  approved: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  rejected: 'bg-red-50 text-red-800 ring-red-200',
  expired: 'bg-gray-100 text-gray-700 ring-gray-200',
  takedown: 'bg-red-50 text-red-800 ring-red-200',
  archived: 'bg-slate-100 text-slate-700 ring-slate-300',
};

const CATEGORIES = [
  { value: 'incident_factual', label: 'Incident (factual)' },
  { value: 'safety_tip', label: 'Safety tip' },
  { value: 'lost_pet', label: 'Lost pet' },
  { value: 'community_alert', label: 'Community alert' },
];

const AdminSafetyBulletin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Default to the "Live" tab so admins immediately see currently-published
  // alerts with their Edit / Archive / Delete controls (the wording on the
  // submit form promises this). "Needs review" is usually empty, which made
  // the edit/remove tools look missing.
  const [tab, setTab] = useState('approved');
  const [items, setItems] = useState([]);
  const [towns, setTowns] = useState([]);
  const [needsReviewCount, setNeedsReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null); // full bulletin
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editTown, setEditTown] = useState('');
  const [editCategory, setEditCategory] = useState('');

  const [archiving, setArchiving] = useState(null);
  const [archiveReason, setArchiveReason] = useState('');

  const [deletingId, setDeletingId] = useState(null);
  const [busy, setBusy] = useState(false);

  // Admin broadcast (all-towns) state
  const [broadcasting, setBroadcasting] = useState(false); // modal open?
  const [bcCategory, setBcCategory] = useState('community_alert');
  const [bcTitle, setBcTitle] = useState('');
  const [bcBody, setBcBody] = useState('');
  const [bcLanguage, setBcLanguage] = useState('en');
  // Iter 203 — Town QR generator (admin tool).
  const [qrTown, setQrTown] = useState('');
  const [qrOpen, setQrOpen] = useState(false);
  // Community Safety Hub — admin content sections (alerts | reports | incidents)
  const [section, setSection] = useState('alerts');

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/admin/login'); }
  }, [user, navigate]);

  const headers = user ? { Authorization: `Bearer ${user.token}` } : {};

  const loadTowns = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/safety-bulletin/towns`);
      setTowns(r.data.towns || []);
    } catch { /* non-fatal */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/safety-bulletin`, {
        params: { status: tab, limit: 200 },
        headers,
      });
      setItems(r.data.bulletins || []);
      setNeedsReviewCount(r.data.needsReviewCount ?? r.data.pendingCount ?? 0);
    } catch (e) {
      toast.error('Could not load safety alerts');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  useEffect(() => { if (user?.role === 'admin') { loadTowns(); load(); } }, [loadTowns, load, user]);

  const openEdit = (b) => {
    setEditing(b);
    setEditTitle(b.title || '');
    setEditBody(b.body || '');
    setEditTown(b.town || '');
    setEditCategory(b.category || '');
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await axios.put(
        `${API}/admin/safety-bulletin/${editing.id}`,
        { title: editTitle, body: editBody, town: editTown, category: editCategory },
        { headers },
      );
      toast.success('Alert updated');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  // Iter 204 — Cancel a queued (scheduled) post before it publishes.
  const cancelSchedule = async (b) => {
    if (!b) return;
    if (!window.confirm(`Cancel scheduled publish of "${b.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/safety-bulletin/${b.id}/cancel-schedule`,
        { reason: 'Cancelled by admin' },
        { headers },
      );
      toast.success('Scheduled publish cancelled');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel');
    } finally {
      setBusy(false);
    }
  };

  const doArchive = async () => {
    if (!archiving) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/safety-bulletin/${archiving.id}/archive`,
        { reason: archiveReason.trim() || 'Hidden by admin' },
        { headers },
      );
      toast.success('Alert archived (hidden from public)');
      setArchiving(null);
      setArchiveReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to archive');
    } finally {
      setBusy(false);
    }
  };

  const doUnarchive = async (b) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/safety-bulletin/${b.id}/unarchive`, {}, { headers });
      toast.success('Alert re-published');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to re-publish');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!deletingId) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/safety-bulletin/${deletingId}`, { headers });
      toast.success('Alert deleted');
      setDeletingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  const doPin = async (b) => {
    const hoursStr = window.prompt('Pin for how many hours? (1–168, default 24)', '24');
    if (hoursStr === null) return;
    const hours = parseInt(hoursStr, 10);
    if (!hours || hours < 1 || hours > 168) {
      toast.error('Hours must be between 1 and 168');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/safety-bulletin/${b.id}/pin`, { hours }, { headers });
      toast.success(`Pinned to top for ${hours} h`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to pin');
    } finally {
      setBusy(false);
    }
  };

  const doUnpin = async (b) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/safety-bulletin/${b.id}/unpin`, {}, { headers });
      toast.success('Unpinned');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to unpin');
    } finally {
      setBusy(false);
    }
  };

  const doBroadcast = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/safety-bulletin/broadcast`,
        { title: bcTitle, body: bcBody, category: bcCategory, language: bcLanguage },
        { headers },
      );
      toast.success('Broadcast sent to ALL towns');
      setBroadcasting(false);
      setBcTitle('');
      setBcBody('');
      setBcCategory('community_alert');
      setBcLanguage('en');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to broadcast');
    } finally {
      setBusy(false);
    }
  };

  const tabs = [
    { key: 'hidden', label: `Needs review (${needsReviewCount})`, hint: 'Reported 5+ times or archived' },
    { key: 'scheduled', label: 'Scheduled', hint: 'Queued for future publish (Town Hall / Police)' },
    { key: 'approved', label: 'Live', hint: 'Currently public' },
    { key: 'archived', label: 'Archived', hint: 'Hidden by admin' },
    { key: 'expired', label: 'Expired', hint: 'Past expiresAt' },
    { key: 'cancelled', label: 'Cancelled', hint: 'Scheduled posts cancelled before publish' },
    { key: 'all', label: 'All' },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-safety-bulletin-page">
      <header className="bg-slate-900 text-white shadow-lg">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/dashboard')} className="p-2 rounded hover:bg-white/10" data-testid="admin-safety-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <ShieldAlert className="h-5 w-5" /> Community Safety Hub — Admin
              </h1>
              <p className="text-xs text-slate-300">
                Safety alerts publish instantly. Use this page to edit, hide (archive) or delete any post.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Iter 203 — Town QR Generator */}
            <select
              value={qrTown}
              onChange={(e) => setQrTown(e.target.value)}
              className="px-2 py-1.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 [&>option]:text-slate-900"
              data-testid="admin-town-qr-select"
              aria-label="Pick a town to generate a QR code"
            >
              <option value="">Town QR…</option>
              {towns.map(tn => <option key={tn} value={tn}>{tn}</option>)}
            </select>
            <button
              onClick={() => { if (qrTown) setQrOpen(true); }}
              disabled={!qrTown}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-medium"
              title="Generate a QR code for this town's Safety Alerts page"
              data-testid="admin-town-qr-generate-btn"
            >
              <QrCode className="h-4 w-4" /> Generate QR
            </button>
            <button
              onClick={() => setBroadcasting(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-sm font-medium"
              title="Send a safety alert to every town (admin only)"
              data-testid="admin-safety-broadcast-btn"
            >
              <Megaphone className="h-4 w-4" /> Broadcast (All towns)
            </button>
            <button onClick={load} className="p-2 rounded hover:bg-white/10" title="Refresh" data-testid="admin-safety-refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Content-type sections — the hub has three managed content types */}
      <div className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-5xl mx-auto px-4 flex gap-1 overflow-x-auto" data-testid="admin-hub-sections">
          {[
            { key: 'alerts', label: 'Official Alerts' },
            { key: 'reports', label: 'Community Reports & Help' },
            { key: 'incidents', label: 'Incident Rooms' },
          ].map(s => (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                section === s.key ? 'border-amber-400 text-white' : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
              data-testid={`admin-hub-section-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {section === 'reports' && (
        <main className="max-w-5xl mx-auto px-4 py-6">
          <AdminCommunityReportsPanel headers={headers} towns={towns} />
        </main>
      )}
      {section === 'incidents' && (
        <main className="max-w-5xl mx-auto px-4 py-6">
          <AdminIncidentsPanel headers={headers} />
        </main>
      )}

      {section === 'alerts' && (<>
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 flex gap-1 overflow-x-auto" data-testid="admin-safety-tabs">
          {tabs.map(tt => (
            <button
              key={tt.key}
              onClick={() => setTab(tt.key)}
              title={tt.hint || ''}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                tab === tt.key ? 'border-slate-900 text-slate-900' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`admin-safety-tab-${tt.key}`}
            >
              {tt.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center text-gray-500 py-12">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-gray-200" data-testid="admin-safety-empty">
            <ShieldAlert className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">Nothing here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(b => (
              <article key={b.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid={`admin-safety-item-${b.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ring-1 ${STATUS_BADGES[b.status] || ''}`}>
                      {b.status}
                    </span>
                    {b.hiddenForReview && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-800 ring-1 ring-red-200">
                        <EyeOff className="h-3 w-3" /> hidden
                      </span>
                    )}
                    {b.pinnedUntil && b.pinnedUntil > new Date().toISOString() && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-900 ring-1 ring-amber-300"
                        title={`Pinned until ${b.pinnedUntil.slice(0, 16).replace('T', ' ')}`}
                      >
                        <Pin className="h-3 w-3" /> pinned
                      </span>
                    )}
                    {/* Iter 204 — Priority + scheduled badges */}
                    {b.priority && b.priority !== 'normal' && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ring-1 ${
                          b.priority === 'critical' ? 'bg-red-100 text-red-800 ring-red-300'
                          : b.priority === 'high' ? 'bg-orange-100 text-orange-800 ring-orange-300'
                          : 'bg-slate-100 text-slate-600 ring-slate-300'
                        }`}
                        data-testid={`admin-safety-priority-${b.id}`}
                      >
                        {b.priority}
                      </span>
                    )}
                    {b.status === 'scheduled' && b.scheduledAt && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-800 ring-1 ring-indigo-300"
                        data-testid={`admin-safety-scheduled-${b.id}`}
                        title={`Will publish at ${b.scheduledAt}`}
                      >
                        ⏱ {b.scheduledAt.slice(0, 16).replace('T', ' ')}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">
                      {b.town === '__ALL_TOWNS__' ? 'All towns (broadcast)' : b.town}
                    </span>
                    <span className="text-xs text-gray-500">· {b.category}</span>
                    <span className="text-xs text-gray-500">· {b.language?.toUpperCase()}</span>
                    {b.reportCount > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                        {b.reportCount} report(s)
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">{b.createdAt?.slice(0, 16).replace('T', ' ')}</span>
                </div>
                <h2 className="text-base font-semibold text-gray-900 mb-1">{b.title}</h2>
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed mb-3">{b.body}</p>
                <div className="flex flex-wrap gap-3 text-xs text-gray-500 border-t border-gray-100 pt-2 mb-3">
                  <span>Author: <strong className="text-gray-700">{b.authorName || '—'}</strong></span>
                  <span>{b.authorEmail}</span>
                  {b.expiresAt && <span>Expires: {b.expiresAt.slice(0, 16).replace('T', ' ')}</span>}
                  {b.archiveReason && <span className="text-red-700">Archive reason: {b.archiveReason}</span>}
                  {(b.pushSent !== undefined || b.inboxUsers !== undefined) && (
                    <span className="text-slate-700">Notified: {b.pushSent || 0} push · {b.inboxUsers || 0} users · {b.inboxAnon || 0} anon · {b.emailSent || 0} email</span>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {(() => {
                    const nowIso = new Date().toISOString();
                    const isPinned = b.pinnedUntil && b.pinnedUntil > nowIso;
                    return isPinned ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => doUnpin(b)}
                        disabled={busy}
                        className="border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
                        data-testid={`admin-safety-unpin-${b.id}`}
                        title={`Pinned until ${b.pinnedUntil.slice(0, 16).replace('T', ' ')}`}
                      >
                        <PinOff className="h-4 w-4 mr-1" /> Unpin
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => doPin(b)}
                        disabled={busy || b.status === 'archived' || b.status === 'expired'}
                        className="border-amber-300 text-amber-800 hover:bg-amber-50"
                        data-testid={`admin-safety-pin-${b.id}`}
                        title="Pin to top of the public feed for 24 h"
                      >
                        <Pin className="h-4 w-4 mr-1" /> Pin 24 h
                      </Button>
                    );
                  })()}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(b)}
                    disabled={busy}
                    data-testid={`admin-safety-edit-${b.id}`}
                  >
                    <Edit3 className="h-4 w-4 mr-1" /> Edit
                  </Button>
                  {b.status === 'approved' && !b.hiddenForReview && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setArchiving(b); setArchiveReason(''); }}
                      disabled={busy}
                      className="border-amber-300 text-amber-800 hover:bg-amber-50"
                      data-testid={`admin-safety-archive-${b.id}`}
                    >
                      <Archive className="h-4 w-4 mr-1" /> Archive / Hide
                    </Button>
                  )}
                  {b.status === 'scheduled' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => cancelSchedule(b)}
                      disabled={busy}
                      className="border-rose-300 text-rose-800 hover:bg-rose-50"
                      data-testid={`admin-safety-cancel-schedule-${b.id}`}
                      title={`Cancel scheduled publish at ${(b.scheduledAt || '').slice(0, 16).replace('T', ' ')}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" /> Cancel scheduled
                    </Button>
                  )}
                  {(b.status === 'archived' || b.hiddenForReview) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => doUnarchive(b)}
                      disabled={busy}
                      className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                      data-testid={`admin-safety-unarchive-${b.id}`}
                    >
                      <RotateCcw className="h-4 w-4 mr-1" /> Re-publish
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDeletingId(b.id)}
                    disabled={busy}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid={`admin-safety-delete-${b.id}`}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
      </>)}

      {/* Edit dialog — edit any field. Save = single PUT. */}
      <Dialog open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <DialogContent data-testid="admin-safety-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit alert</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 mb-2">All four fields are editable. Sanitisation still applies (URLs stripped, HTML removed).</p>
          <label className="block text-xs font-medium text-gray-700 mt-2">Town</label>
          <select
            value={editTown}
            onChange={(e) => setEditTown(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            data-testid="admin-safety-edit-town"
          >
            {towns.map(tn => <option key={tn} value={tn}>{tn}</option>)}
          </select>
          <label className="block text-xs font-medium text-gray-700 mt-2">Category</label>
          <select
            value={editCategory}
            onChange={(e) => setEditCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            data-testid="admin-safety-edit-category"
          >
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <label className="block text-xs font-medium text-gray-700 mt-2">Title</label>
          <Input
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value.slice(0, 80))}
            maxLength={80}
            data-testid="admin-safety-edit-title"
          />
          <label className="block text-xs font-medium text-gray-700 mt-2">Body</label>
          <Textarea
            value={editBody}
            onChange={(e) => setEditBody(e.target.value.slice(0, 500))}
            rows={5}
            maxLength={500}
            data-testid="admin-safety-edit-body"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="admin-safety-edit-cancel">Cancel</Button>
            <Button
              onClick={saveEdit}
              disabled={busy || editTitle.length < 4 || editBody.length < 10 || !editTown || !editCategory}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="admin-safety-edit-save"
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive dialog */}
      <Dialog open={!!archiving} onOpenChange={(open) => { if (!open) setArchiving(null); }}>
        <DialogContent data-testid="admin-safety-archive-dialog">
          <DialogHeader>
            <DialogTitle>Archive / Hide alert</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-gray-500 mb-2">The alert stops appearing in the public feed but the record is kept for audit. You can re-publish it at any time.</p>
          <Textarea
            value={archiveReason}
            onChange={(e) => setArchiveReason(e.target.value.slice(0, 500))}
            placeholder="Optional: reason for archiving (kept on the audit record)"
            rows={3}
            maxLength={500}
            data-testid="admin-safety-archive-reason"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiving(null)} data-testid="admin-safety-archive-cancel">Cancel</Button>
            <Button
              onClick={doArchive}
              disabled={busy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="admin-safety-archive-confirm"
            >
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deletingId} onOpenChange={(open) => { if (!open) setDeletingId(null); }}>
        <DialogContent data-testid="admin-safety-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete alert</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700">Permanently delete this alert and all its reports. <strong>This cannot be undone.</strong></p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingId(null)} data-testid="admin-safety-delete-cancel">Cancel</Button>
            <Button
              onClick={doDelete}
              disabled={busy}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="admin-safety-delete-confirm"
            >
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broadcast (All towns) modal — admin only */}
      <Dialog open={broadcasting} onOpenChange={(open) => { if (!open) setBroadcasting(false); }}>
        <DialogContent data-testid="admin-safety-broadcast-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-fuchsia-600" /> Broadcast — All towns
            </DialogTitle>
          </DialogHeader>
          <div className="bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3 text-xs text-fuchsia-900 mb-2">
            <strong>Admin-only.</strong> Delivered to <strong>every active subscriber</strong> regardless of their area subscriptions. Uses push + inbox + email (if enabled). Appears in the public feed labelled "All towns".
          </div>
          <label className="block text-xs font-medium text-gray-700 mt-2">Category</label>
          <select
            value={bcCategory}
            onChange={(e) => setBcCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            data-testid="admin-safety-broadcast-category"
          >
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <label className="block text-xs font-medium text-gray-700 mt-2">Language</label>
          <select
            value={bcLanguage}
            onChange={(e) => setBcLanguage(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            data-testid="admin-safety-broadcast-language"
          >
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
          <label className="block text-xs font-medium text-gray-700 mt-2">Title</label>
          <Input
            value={bcTitle}
            onChange={(e) => setBcTitle(e.target.value.slice(0, 80))}
            maxLength={80}
            placeholder="Short, factual title"
            data-testid="admin-safety-broadcast-title"
          />
          <label className="block text-xs font-medium text-gray-700 mt-2">Body</label>
          <Textarea
            value={bcBody}
            onChange={(e) => setBcBody(e.target.value.slice(0, 500))}
            rows={5}
            maxLength={500}
            placeholder="Body — no personal data, no names, no plates"
            data-testid="admin-safety-broadcast-body"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setBroadcasting(false)} data-testid="admin-safety-broadcast-cancel">Cancel</Button>
            <Button
              onClick={doBroadcast}
              disabled={busy || bcTitle.length < 4 || bcBody.length < 10}
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
              data-testid="admin-safety-broadcast-confirm"
            >
              <Megaphone className="h-4 w-4 mr-1" /> Broadcast to all towns
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 203 — Town QR generator modal */}
      <TownQRModal open={qrOpen} onClose={() => setQrOpen(false)} town={qrTown} />
    </div>
  );
};

export default AdminSafetyBulletin;
